import {partition} from 'ramda';

import {
  newVehicleApi,
  usePutColourTrimsMutation,
  usePutTrimFeatureAssignmentsMutation,
  useUpdatePackageMutation,
  type OfferedTrimResponseBody,
  type TrimFeatureAssignment,
  type TrimFeatureAssignmentInput,
  type TrimResponseBody,
} from '@dms/api/new-vehicle';

import {t} from '../utils/catalogueUi';
import {toOfferedTrims, type OfferedTrimRow} from '../utils/offeredTrimRows';

const isVariantLimited = (assignment: TrimFeatureAssignment) =>
  assignment.appliesIf.anyOfVariantIds.length > 0;

const toCopiedAssignment = (
  assignment: TrimFeatureAssignment,
  canViewCost: boolean
): TrimFeatureAssignmentInput => ({
  featureId: assignment.featureId,
  inclusion: assignment.inclusion,
  appliesIf: assignment.appliesIf,
  priceIf: assignment.priceIf
    .filter(({condition}) => condition.anyOfVariantIds.length === 0)
    .map(({condition, priceNet}) => ({condition, priceNet})),
  variantPriceOverrides: [],
  priceNet: assignment.priceNet,
  ...(canViewCost ? {costNet: assignment.costNet ?? null} : {}),
  sortOrder: assignment.sortOrder,
});

const copiedRow = (source: OfferedTrimResponseBody, targetTrimId: string): OfferedTrimRow => ({
  trimId: targetTrimId,
  isOffered: true,
  priceNet: source.priceNet,
  costNet: source.costNet ?? '',
});

/**
 * "Copy from trim" for a trim just created: the source trim's equipment, colours and packages with
 * their prices, through the same writes the tabs use. The writes run one at a time (the backend
 * refuses concurrent writes in a model year) and a failed one does not stop the others; resolves
 * the names of what was not copied, costs included when the user cannot see them, and whether every
 * write went through (variant-limited items and costs are left out by design, not by a failure).
 */
export function useCopyFromTrim(canViewCost: boolean) {
  const [getAssignments] = newVehicleApi.useLazyListTrimFeatureAssignmentsQuery();
  const [listFeatures] = newVehicleApi.useLazyListFeatureQuery();
  const [listColours] = newVehicleApi.useLazyListColourQuery();
  const [listPackages] = newVehicleApi.useLazyListPackageQuery();
  const [putAssignments] = usePutTrimFeatureAssignmentsMutation();
  const [putColourTrims] = usePutColourTrimsMutation();
  const [updatePackage] = useUpdatePackageMutation();

  return async (sourceTrimId: string, target: TrimResponseBody) => {
    const notCopied: string[] = [];
    let isComplete = true;
    const copy = (name: string, write: () => Promise<unknown>) =>
      write().catch(() => {
        isComplete = false;
        notCopied.push(name);
      });
    const ofYear = <T extends {modelYearId: string; archived: boolean}>(items: T[]) =>
      items.filter((item) => item.modelYearId === target.modelYearId && !item.archived);

    await copy(t('entity.newVehicle.catalogue.equipment', 'Equipment'), async () => {
      const assignments = await getAssignments({trimId: sourceTrimId}, false).unwrap();
      const [limited, copied] = partition(isVariantLimited, assignments);
      if (copied.length > 0) {
        await putAssignments({
          trimId: target.id,
          replaceTrimFeatureAssignmentsRequestBody: {
            assignments: copied.map((row) => toCopiedAssignment(row, canViewCost)),
            version: target.version,
          },
        }).unwrap();
      }
      if (limited.length > 0) {
        const {items} = await listFeatures({modelYearId: target.modelYearId}, true).unwrap();
        notCopied.push(
          ...limited.map(({featureId}) => items.find(({id}) => id === featureId)?.name ?? featureId)
        );
      }
    });
    await copy(t('entity.newVehicle.authoring.colors', 'Colours'), async () => {
      const colours = ofYear((await listColours({}, false).unwrap()).items);
      for (const colour of colours) {
        const source = colour.trims.find(({trimId}) => trimId === sourceTrimId);
        if (source) {
          await copy(colour.name, () =>
            putColourTrims({
              colourId: colour.id,
              replaceColourTrimsRequestBody: {
                trims: toOfferedTrims([copiedRow(source, target.id)], colour.trims, canViewCost),
                version: colour.version,
              },
            }).unwrap()
          );
        }
      }
    });
    await copy(t('entity.newVehicle.catalogue.packages', 'Packages'), async () => {
      const packages = ofYear((await listPackages({}, false).unwrap()).items).filter(
        ({status}) => status !== 'archived'
      );
      for (const item of packages) {
        const source = item.trims.find(({trimId}) => trimId === sourceTrimId);
        if (source) {
          await copy(item.name, () =>
            updatePackage({
              packageId: item.id,
              packageWriteRequestBody: {
                modelYearId: item.modelYearId,
                name: item.name,
                codes: item.codes,
                description: item.description,
                memberFeatureIds: item.memberFeatureIds,
                trims: toOfferedTrims([copiedRow(source, target.id)], item.trims, canViewCost),
                status: item.status,
                version: item.version,
              },
            }).unwrap()
          );
        }
      }
    });
    if (!canViewCost) {
      notCopied.push(t('entity.newVehicle.catalogue.costs', 'Costs'));
    }
    return {notCopied, isComplete};
  };
}
